import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Shape of the authenticated user as returned by the backend (/api/v1/auth/me
// and the login exchange endpoints). Keep this loose — the backend auth agent
// owns the canonical shape; we only depend on these fields in the shell.
export interface AuthUser {
  id: string;
  email: string;
  display_name?: string;
  photo_url?: string | null;
  is_admin?: boolean;
  // BP 26 — student identity + password lifecycle.
  student_id?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  is_instructor?: boolean;
  must_change_password?: boolean;
  // College accounts are minimized: no real email, name = initials.
  email_is_placeholder?: boolean;
  identity_minimized?: boolean;
  learner_status?: 'active' | 'withdrawn' | 'completed';
  // User preferences surfaced in Settings (voice / theme / model).
  preferred_voice?: string | null;
  preferred_theme?: 'paper' | 'light' | 'dark' | null;
  preferred_ai_model?: string | null;
  [key: string]: unknown;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  setAuth: (token: string, user: AuthUser | null) => void;
  setUser: (user: AuthUser | null) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      setAuth: (token, user) => set({ token, user }),
      setUser: (user) => set({ user }),
      logout: () => set({ token: null, user: null }),
      isAuthenticated: () => !!get().token,
    }),
    {
      name: 'acechem-auth',
      // Persist only the token; the user object is re-hydrated from /auth/me on
      // load so it never goes stale in localStorage.
      partialize: (s) => ({ token: s.token }),
    }
  )
);

// Non-hook accessor for use in the axios interceptor (outside React).
export const getAuthToken = () => useAuthStore.getState().token;
